import { useRef, useState } from 'react'
import { Download, FileUp, Loader2, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { PageHeader, Forbidden } from '@/components'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  type ImportPreview,
  type ImportResult,
  type ImportRowError,
  triggerTemplateDownload,
  useApplyImport,
  usePreviewImport,
} from '@/hooks/useImport'

// ---------------------------------------------------------------------------
// Small helpers for normalizing RowError fields. Backend emits snake_case
// (sheet / row / column / message).
// ---------------------------------------------------------------------------

function rowErrorSheet(e: ImportRowError): string {
  return e.sheet ?? ''
}
function rowErrorRow(e: ImportRowError): number {
  return e.row ?? 0
}
function rowErrorColumn(e: ImportRowError): string {
  return e.column ?? ''
}
function rowErrorMessage(e: ImportRowError): string {
  return e.message ?? ''
}

function errorRows(preview: ImportPreview): ImportRowError[] {
  return [
    ...preview.buildings.errors.map((e) => ({ ...e, sheet: rowErrorSheet(e) || 'Buildings' })),
    ...preview.rooms.errors.map((e) => ({ ...e, sheet: rowErrorSheet(e) || 'Rooms' })),
  ]
}

// ---------------------------------------------------------------------------

export function ImportsPage() {
  const { user } = useAuth()
  const { effectiveOrgId } = useOrg()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [downloading, setDownloading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const previewMutation = usePreviewImport(effectiveOrgId)
  const applyMutation = useApplyImport(effectiveOrgId)

  // Admin gate — canonical in-page <Forbidden /> pattern. Sidebar
  // already hides this item for non-admins; this guard covers
  // direct-URL navigation.
  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return <Forbidden />
  }

  // Step 1: download template.
  async function onDownloadTemplate() {
    if (!effectiveOrgId) {
      toast.error('Select an organization first')
      return
    }
    setDownloading(true)
    try {
      await triggerTemplateDownload(effectiveOrgId)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to download template'
      )
    } finally {
      setDownloading(false)
    }
  }

  // Step 3: file selection + preview.
  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null
    setSelectedFile(file)
    setPreview(null)
    setResult(null)
  }

  async function onUploadAndPreview() {
    if (!selectedFile) {
      toast.error('Pick a file to upload')
      return
    }
    if (!effectiveOrgId) {
      toast.error('Select an organization first')
      return
    }
    try {
      const p = await previewMutation.mutateAsync(selectedFile)
      setPreview(p)
      setResult(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Preview failed')
    }
  }

  async function onConfirmApply() {
    if (!selectedFile) {
      toast.error('No file to apply')
      return
    }
    try {
      const r = await applyMutation.mutateAsync(selectedFile)
      setResult(r)
      setPreview(null)
      toast.success(
        `Created ${r.summary.buildings_create} building${
          r.summary.buildings_create === 1 ? '' : 's'
        } and ${r.summary.rooms_create} room${
          r.summary.rooms_create === 1 ? '' : 's'
        }`
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Apply failed')
    }
  }

  function onCancelPreview() {
    setPreview(null)
  }

  function onDone() {
    setResult(null)
    setSelectedFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const applyDisabled =
    !preview || applyMutation.isPending || preview.summary.errors_total > 0

  return (
    <div className="space-y-6">
      <PageHeader
        title="Imports"
        description="Import buildings and rooms in bulk"
      />

      {/* Step 1 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Step 1. Download template</CardTitle>
          <CardDescription>
            Start with a fresh template file. The Buildings sheet sets up
            dropdowns on the Rooms sheet so the Building column is typo-proof.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={onDownloadTemplate}
            disabled={downloading || !effectiveOrgId}
            aria-busy={downloading}
          >
            {downloading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Download template
          </Button>
        </CardContent>
      </Card>

      {/* Step 2 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Step 2. Fill in the Buildings and Rooms sheets
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal space-y-1 pl-6 text-sm text-muted-foreground">
            <li>Fill in the <strong>Buildings</strong> sheet first. Name is required.</li>
            <li>
              Then fill in the <strong>Rooms</strong> sheet. The Building column
              is a dropdown of the names you just entered.
            </li>
            <li>Floor must be a whole number (0 = ground floor).</li>
            <li>
              Re-uploading the same file is safe — duplicates are skipped, not
              overwritten.
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Step 3 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Step 3. Upload and preview</CardTitle>
          <CardDescription>
            We'll parse the file and show you what will be created before
            anything is written.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-2">
              <label
                htmlFor="import-file-input"
                className="text-sm font-medium"
              >
                Import file
              </label>
              <input
                id="import-file-input"
                ref={fileInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={onFileChange}
                className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
              />
              {selectedFile && (
                <p className="text-xs text-muted-foreground">
                  {selectedFile.name} · {Math.round(selectedFile.size / 1024)} KB
                </p>
              )}
            </div>
            <Button
              onClick={onUploadAndPreview}
              disabled={
                !selectedFile || previewMutation.isPending || !effectiveOrgId
              }
              aria-busy={previewMutation.isPending}
            >
              {previewMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileUp className="mr-2 h-4 w-4" />
              )}
              Upload and preview
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview panel */}
      {preview && !result && (
        <Card data-testid="import-preview-panel">
          <CardHeader>
            <CardTitle className="text-lg">Preview</CardTitle>
            <CardDescription>Nothing has been written yet.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <SummaryCard
                title="Buildings"
                toCreate={preview.summary.buildings_create}
                toSkip={preview.summary.buildings_skip}
              />
              <SummaryCard
                title="Rooms"
                toCreate={preview.summary.rooms_create}
                toSkip={preview.summary.rooms_skip}
              />
            </div>

            {preview.summary.errors_total > 0 && (
              <ErrorsTable
                title={`${preview.summary.errors_total} row${
                  preview.summary.errors_total === 1 ? '' : 's'
                } with errors`}
                errors={errorRows(preview)}
              />
            )}

            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={onCancelPreview}>
                Cancel
              </Button>
              <Button
                onClick={onConfirmApply}
                disabled={applyDisabled}
                aria-busy={applyMutation.isPending}
              >
                {applyMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                Confirm and apply
              </Button>
            </div>
            {preview.summary.errors_total > 0 && (
              <p className="text-xs text-destructive">
                Fix the row errors in your file and re-upload before applying.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Result panel */}
      {result && (
        <Card data-testid="import-result-panel">
          <CardHeader>
            <CardTitle className="text-lg">Import complete</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <SummaryCard
                title="Buildings"
                toCreate={result.summary.buildings_create}
                toSkip={result.summary.buildings_skip}
                createdLabel="Created"
                skippedLabel="Skipped"
              />
              <SummaryCard
                title="Rooms"
                toCreate={result.summary.rooms_create}
                toSkip={result.summary.rooms_skip}
                createdLabel="Created"
                skippedLabel="Skipped"
              />
            </div>

            {result.summary.errors_total > 0 && (
              <ErrorsTable
                title={`${result.summary.errors_total} row${
                  result.summary.errors_total === 1 ? '' : 's'
                } skipped due to errors`}
                errors={errorRows(result)}
              />
            )}

            {result.batch_errors && result.batch_errors.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-destructive">
                  {result.batch_errors.length} item
                  {result.batch_errors.length === 1 ? '' : 's'} failed to save
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sheet</TableHead>
                      <TableHead>Row</TableHead>
                      <TableHead>Message</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.batch_errors.map((e, i) => (
                      <TableRow key={`batch-${i}`}>
                        <TableCell>{e.sheet}</TableCell>
                        <TableCell>{e.row}</TableCell>
                        <TableCell className="text-destructive">{e.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <Button onClick={onDone}>Done</Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function SummaryCard({
  title,
  toCreate,
  toSkip,
  createdLabel = 'Will create',
  skippedLabel = 'Will skip (already exist)',
}: {
  title: string
  toCreate: number
  toSkip: number
  createdLabel?: string
  skippedLabel?: string
}) {
  return (
    <div className="rounded-lg border bg-muted/30 p-4">
      <p className="text-sm font-medium">{title}</p>
      <dl className="mt-2 grid grid-cols-2 gap-1 text-sm">
        <dt className="text-muted-foreground">{createdLabel}</dt>
        <dd className="text-right font-semibold">{toCreate}</dd>
        <dt className="text-muted-foreground">{skippedLabel}</dt>
        <dd className="text-right">{toSkip}</dd>
      </dl>
    </div>
  )
}

function ErrorsTable({
  title,
  errors,
}: {
  title: string
  errors: ImportRowError[]
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-destructive">{title}</p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Sheet</TableHead>
            <TableHead>Row</TableHead>
            <TableHead>Column</TableHead>
            <TableHead>Message</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {errors.map((e, i) => (
            <TableRow key={`err-${i}`}>
              <TableCell>{rowErrorSheet(e)}</TableCell>
              <TableCell>{rowErrorRow(e)}</TableCell>
              <TableCell>{rowErrorColumn(e)}</TableCell>
              <TableCell>{rowErrorMessage(e)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
